import './styles.css';
import { createSharedSheetController, type SharedSheetController } from '../sheet/controller';

const SELECT_SELECTOR = 'select.select-sheet';
const BOUND_KEY = 'true';

type SelectState = {
    select: HTMLSelectElement;
    trigger: HTMLButtonElement;
    sheet: HTMLElement;
    panel: HTMLElement;
    backdrop: HTMLButtonElement;
    list: HTMLElement;
    title: HTMLElement;
    closeBtn: HTMLButtonElement;
    controller: SharedSheetController;
};

let initialized = false;
const states = new WeakMap<HTMLSelectElement, SelectState>();

export function initSelectComponent(): void {
    if (initialized) return;
    initialized = true;
    document.addEventListener('ui:update', () => mount(document));
}

function mount(scope: ParentNode): void {
    const selects = Array.from(scope.querySelectorAll<HTMLSelectElement>(SELECT_SELECTOR));
    selects.forEach((select) => {
        if (select.dataset.selectSheetBound === BOUND_KEY) return;
        select.dataset.selectSheetBound = BOUND_KEY;
        const state = createState(select);
        if (!state) return;
        states.set(select, state);
        bind(state);
        syncTriggerLabel(state);
    });
}

function createState(select: HTMLSelectElement): SelectState | null {
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'select-sheet-trigger select select-bordered';
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', 'false');

    const sheet = document.createElement('section');
    sheet.className = 'sheet select-sheet';
    sheet.setAttribute('aria-hidden', 'true');
    sheet.hidden = true;

    const backdrop = document.createElement('button');
    backdrop.type = 'button';
    backdrop.className = 'sheet-backdrop select-sheet-backdrop';
    backdrop.setAttribute('aria-label', '閉じる');

    const panel = document.createElement('div');
    panel.className = 'sheet-panel select-sheet-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    const handle = document.createElement('div');
    handle.className = 'select-sheet-handle handle-bar';
    handle.setAttribute('aria-hidden', 'true');

    const header = document.createElement('header');
    header.className = 'select-sheet-header';

    const title = document.createElement('h3');
    title.className = 'select-sheet-title';
    title.textContent = select.dataset.placeholder || '選択してください';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'select-sheet-close';
    closeBtn.textContent = '閉じる';

    const list = document.createElement('div');
    list.className = 'sheet-content select-sheet-list';

    header.append(title, closeBtn);
    panel.append(handle, header, list);
    sheet.append(backdrop, panel);

    select.insertAdjacentElement('afterend', trigger);
    document.body.appendChild(sheet);
    rebuildOptions(select, list);

    const controller = createSharedSheetController({
        root: sheet,
        trigger,
        panel,
        backdrop,
        onBeforeOpen: () => {
            rebuildOptions(select, list);
            updateSelectedStateByElements(select, list);
            // 選択済み項目が見えるようにスクロール
            const selected = list.querySelector<HTMLElement>('.select-sheet-option.is-selected');
            if (selected) {
                requestAnimationFrame(() => selected.scrollIntoView({ block: 'nearest' }));
            }
        },
    });

    return { select, trigger, sheet, panel, backdrop, list, title, closeBtn, controller };
}

function bind(state: SelectState): void {
    state.trigger.addEventListener('click', () => {
        if (!isMobile()) return;
        state.controller.open();
    });

    // select → select-sheet: change / input の両方を監視して sheet に反映
    // change: ネイティブ操作・外部 JS が dispatchEvent するケース
    // input:  React / Stimulus 等のフレームワークが input のみ発火するケース
    // 同一マイクロタスク内で両イベントが連続して来ても 1 回だけ実行する
    let syncScheduled = false;
    const scheduleSync = () => {
        if (syncScheduled) return;
        syncScheduled = true;
        queueMicrotask(() => {
            syncScheduled = false;
            syncTriggerLabel(state);
            updateSelectedState(state);
        });
    };
    state.select.addEventListener('change', scheduleSync);
    state.select.addEventListener('input', scheduleSync);

    // ── 検知レイヤー 1: MutationObserver ──────────────────────────────────────
    // ブラウザ組み込みオートフィルが <option selected> 属性を直接変更するケースを捕捉
    const attrObserver = new MutationObserver(scheduleSync);
    attrObserver.observe(state.select, {
        subtree: true,
        attributes: true,
        attributeFilter: ['selected'],
        childList: true,
    });

    // ── 検知レイヤー 2: value セッター傍受 ──────────────────────────────────
    // JS ライブラリ（郵便番号補完等）が select.value = 'x' を
    // イベント・属性変化なしに書き換えるケースを捕捉し、select-placeholder も含めて同期する
    const nativeDesc = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
    if (nativeDesc?.set) {
        Object.defineProperty(state.select, 'value', {
            get() { return nativeDesc.get!.call(this); },
            set(v: string) {
                const prev = nativeDesc.get!.call(this) as string;
                nativeDesc.set!.call(this, v);
                if ((nativeDesc.get!.call(this) as string) !== prev) scheduleSync();
            },
            configurable: true,
            enumerable: true,
        });
    }

    // select が DOM から除去されたら observer 切断・own property 削除でリークを防ぐ
    state.select.addEventListener('remove', () => {
        attrObserver.disconnect();
        if (Object.prototype.hasOwnProperty.call(state.select, 'value')) {
            delete (state.select as unknown as Record<string, unknown>).value;
        }
    }, { once: true });

    state.backdrop.addEventListener('click', () => state.controller.close());
    state.closeBtn.addEventListener('click', () => state.controller.close());

    // select-sheet → select: 選択値を select に書き戻して双方向を完結させる
    state.list.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        const optionBtn = target.closest<HTMLButtonElement>('[data-select-sheet-option]');
        if (!optionBtn) return;
        const value = optionBtn.dataset.value ?? '';
        state.select.value = value;
        // input → change の順で発火（ネイティブ select と同じ順序）
        state.select.dispatchEvent(new Event('input', { bubbles: true }));
        state.select.dispatchEvent(new Event('change', { bubbles: true }));
        state.controller.close();
    });
}

function rebuildOptions(select: HTMLSelectElement, list: HTMLElement): void {
    list.innerHTML = '';
    Array.from(select.children).forEach((child) => {
        if (child instanceof HTMLOptGroupElement) {
            const group = document.createElement('div');
            group.className = 'select-sheet-group';

            const label = document.createElement('div');
            label.className = 'select-sheet-group-label';
            label.textContent = child.label;
            group.appendChild(label);

            Array.from(child.children).forEach((groupChild) => {
                if (!(groupChild instanceof HTMLOptionElement)) return;
                group.appendChild(createOptionButton(groupChild, select.value, child.disabled));
            });

            list.appendChild(group);
            return;
        }

        if (child instanceof HTMLOptionElement) {
            list.appendChild(createOptionButton(child, select.value));
        }
    });
}

function createOptionButton(option: HTMLOptionElement, selectedValue: string, groupDisabled = false): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'select-sheet-option';
    btn.dataset.selectSheetOption = 'true';
    btn.dataset.value = option.value;
    btn.textContent = option.textContent || option.label || '';
    if (option.disabled || groupDisabled) btn.disabled = true;
    if (option.value === selectedValue) btn.classList.add('is-selected');
    return btn;
}

function updateSelectedState(state: SelectState): void {
    updateSelectedStateByElements(state.select, state.list);
}

function updateSelectedStateByElements(select: HTMLSelectElement, list: HTMLElement): void {
    const buttons = Array.from(list.querySelectorAll<HTMLButtonElement>('[data-select-sheet-option]'));
    buttons.forEach((btn) => btn.classList.toggle('is-selected', btn.dataset.value === select.value));
}

function syncTriggerLabel(state: SelectState): void {
    const selected = state.select.selectedOptions[0];
    const placeholder = state.select.dataset.placeholder || '選択してください';
    const text = selected?.textContent?.trim() || placeholder;
    const isPlaceholder = !state.select.value;
    state.trigger.textContent = text;
    state.select.classList.toggle('select-placeholder', isPlaceholder);
    state.trigger.classList.toggle('select-placeholder', isPlaceholder);
}

function isMobile(): boolean {
    return window.matchMedia('(max-width: 767px)').matches;
}
